import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout';
import { 
  Users, PawPrint, BookOpen, BarChart3, ArrowRight, CheckCircle, 
  DollarSign, MessageSquare, Send, Heart, Trophy, TrendingUp,
  X, Mail, MapPin, Calendar, ChevronRight, Eye, Clock, AlertCircle, XCircle, Video, Trash2,
  Package, Truck, Search, Filter, Warehouse, ClipboardList
} from 'lucide-react';
import InventoryTab from './InventoryTab';
import SessionsTab from './SessionsTab';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Tab Components =====

function OverviewTab({ analytics, owners, sessions, announcements, selectedOwner, setSelectedOwner, fetchData }) {
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '' });

  const handleSendAnnouncement = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.message.trim()) {
      toast.error('Please fill in both title and message');
      return;
    }
    try {
      await axios.post(`${API}/admin/announcements`, newAnnouncement);
      toast.success('Announcement sent to community!');
      setNewAnnouncement({ title: '', message: '' });
      fetchData();
    } catch { toast.error('Failed to send announcement'); }
  };

  const handleDeleteAnnouncement = async (id) => {
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      toast.success('Announcement deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSessionStatus = async (sessionId, status) => {
    try {
      await axios.patch(`${API}/admin/sessions/${sessionId}?status=${status}`);
      toast.success(`Session ${status}`);
      fetchData();
    } catch { toast.error('Failed to update session'); }
  };

  const handleDeleteOwner = async (ownerId, ownerName) => {
    if (!window.confirm(`Delete ${ownerName} and all their data (dogs, progress, family)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/owners/${ownerId}`);
      toast.success(`${ownerName} has been removed`);
      setSelectedOwner(null);
      fetchData();
    } catch { toast.error('Failed to delete owner'); }
  };

  const SESSION_STATUS = {
    pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
    confirmed: { label: 'Confirmed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
    cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-500', icon: XCircle },
  };

  return (
    <>
      {/* Main Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          { label: 'Owners', value: analytics?.owners_count || 0, icon: Users, bg: 'bg-navy-100', fg: 'text-navy-600' },
          { label: 'Dogs', value: analytics?.dogs_count || 0, icon: PawPrint, bg: 'bg-edupaws-sage/20', fg: 'text-edupaws-sage' },
          { label: 'Families', value: analytics?.families_count || 0, icon: Heart, bg: 'bg-purple-100', fg: 'text-purple-600' },
          { label: 'Completion', value: `${analytics?.completion_rate || 0}%`, icon: CheckCircle, bg: 'bg-edupaws-sage/20', fg: 'text-edupaws-sage' },
          { label: 'Revenue', value: `$${analytics?.total_revenue || 0}`, icon: DollarSign, bg: 'bg-green-100', fg: 'text-green-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.fg}`} />
              </div>
              <div className="text-sm font-body text-gray-500 uppercase tracking-wide">{s.label}</div>
            </div>
            <div className="font-heading text-4xl font-bold text-gray-800">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Videos & Paid Unlocks */}
      <div className="grid md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gradient-to-r from-navy-600 to-navy-700 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-body text-white/70 mb-1">Videos Completed</div>
              <div className="font-heading text-5xl font-bold">{analytics?.total_videos_watched || 0}</div>
              <div className="text-base font-body text-white/70 mt-1">across all users</div>
            </div>
            <BookOpen className="w-12 h-12 text-white/30" />
          </div>
        </div>
        <div className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-base font-body text-white/70 mb-1">Paid Unlocks</div>
              <div className="font-heading text-5xl font-bold">{analytics?.paid_unlocks || 0}</div>
              <div className="text-base font-body text-white/70 mt-1">series purchased</div>
            </div>
            <Trophy className="w-12 h-12 text-white/30" />
          </div>
        </div>
      </div>

      {/* Owner Families */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-gray-800 uppercase">Owner Families & Dogs</h2>
          <span className="text-sm font-body text-gray-500">{owners.length} families registered</span>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {owners.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-body text-base">No owners registered yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {owners.map((owner) => (
                <div
                  key={owner.id}
                  data-testid={`owner-row-${owner.id}`}
                  className="flex items-center justify-between p-4 hover:bg-gray-50/50 transition-colors cursor-pointer group"
                  onClick={() => setSelectedOwner(owner)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-navy-100 rounded-full flex items-center justify-center font-heading font-bold text-navy-600 text-lg">
                      {owner.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-body font-bold text-gray-800 text-base">{owner.name}</h3>
                      <p className="text-sm text-gray-500 font-body flex items-center gap-2">
                        <Mail className="w-3 h-3" /> {owner.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-center">
                      <div className="font-heading font-bold text-gray-800 text-lg">{owner.dogs_count}</div>
                      <div className="text-xs text-gray-500 font-body">Dogs</div>
                    </div>
                    <div className="text-center">
                      <div className="font-heading font-bold text-gray-800 text-lg">{owner.family_count}</div>
                      <div className="text-xs text-gray-500 font-body">Family</div>
                    </div>
                    <div className="text-center">
                      <div className="font-heading font-bold text-edupaws-sage text-lg">{owner.unlocked_videos}</div>
                      <div className="text-xs text-gray-500 font-body">Unlocked</div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-navy-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Owner Detail Modal */}
      <Dialog open={!!selectedOwner} onOpenChange={(open) => !open && setSelectedOwner(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedOwner && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-navy-600 uppercase">
                  {selectedOwner.name}&apos;s Family
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-6">
                <div className="flex items-center gap-4 p-4 bg-navy-50 rounded-xl" data-testid="owner-detail-info">
                  <div className="w-14 h-14 bg-navy-200 rounded-full flex items-center justify-center font-heading font-bold text-navy-600 text-xl">
                    {selectedOwner.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-body font-bold text-gray-800 text-lg">{selectedOwner.name}</h3>
                    <p className="text-sm text-gray-500 font-body">{selectedOwner.email}</p>
                    <p className="text-xs text-gray-400 font-body mt-1">
                      Joined {new Date(selectedOwner.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                  </div>
                </div>
                {selectedOwner.dogs && selectedOwner.dogs.length > 0 && (
                  <div>
                    <h4 className="font-heading font-bold text-gray-700 mb-3 uppercase text-sm">Dogs</h4>
                    <div className="space-y-2">
                      {selectedOwner.dogs.map((dog) => (
                        <div key={dog.id} className="flex items-center gap-3 p-3 bg-sage-50 rounded-xl">
                          <PawPrint className="w-5 h-5 text-edupaws-sage" />
                          <div>
                            <span className="font-body font-bold text-gray-800">{dog.name}</span>
                            <span className="text-sm text-gray-500 font-body ml-2">{dog.breed}, {dog.age} yrs</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button
                  data-testid={`delete-owner-${selectedOwner.id}`}
                  onClick={() => handleDeleteOwner(selectedOwner.id, selectedOwner.name)}
                  className="flex items-center gap-2 text-red-500 hover:text-red-700 font-body font-bold text-sm"
                >
                  <Trash2 className="w-4 h-4" /> Delete User & All Data
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Training Session Requests */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-2xl font-bold text-gray-800 uppercase flex items-center gap-2">
            <Video className="w-6 h-6 text-edupaws-sage" />
            Training Session Requests
          </h2>
          <span className="text-sm font-body text-gray-500">{sessions.filter(s => s.status === 'pending').length} pending</span>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {sessions.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-body text-base">No session requests yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {sessions.map((session) => {
                const statusInfo = SESSION_STATUS[session.status] || SESSION_STATUS.pending;
                const StatusIcon = statusInfo.icon;
                return (
                  <div key={session.id} className="p-4" data-testid={`admin-session-${session.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-edupaws-sage/10 rounded-xl flex items-center justify-center">
                          <PawPrint className="w-5 h-5 text-edupaws-sage" />
                        </div>
                        <div>
                          <h3 className="font-body font-bold text-gray-800 text-base">{session.owner_name}</h3>
                          <p className="text-sm text-gray-500 font-body">
                            Dog: <strong>{session.dog_name}</strong> &bull;{' '}
                            {session.session_type === 'one_on_one' ? '1-on-1 Training' :
                             session.session_type === 'behavior_assessment' ? 'Behavior Assessment' :
                             session.session_type === 'puppy_consultation' ? 'Puppy Consultation' : 'Follow-Up'}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-400 font-body mt-1">
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {session.preferred_date}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {session.preferred_time}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body font-bold ${statusInfo.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                        {session.status === 'pending' && (
                          <div className="flex gap-2">
                            <button data-testid={`confirm-session-${session.id}`} onClick={() => handleSessionStatus(session.id, 'confirmed')}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-body font-bold hover:bg-green-600 transition-colors">Confirm</button>
                            <button onClick={() => handleSessionStatus(session.id, 'cancelled')}
                              className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-xs font-body font-bold hover:bg-gray-300 transition-colors">Decline</button>
                          </div>
                        )}
                        {session.status === 'confirmed' && (
                          <button onClick={() => handleSessionStatus(session.id, 'completed')}
                            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-body font-bold hover:bg-blue-600 transition-colors">Mark Complete</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions & Announcements */}
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-1 space-y-4">
          <h2 className="font-heading text-xl font-bold text-gray-800 uppercase">Quick Actions</h2>
          <Link to="/admin/series" data-testid="manage-content-link"
            className="block bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-navy-200 transition-all group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center"><BookOpen className="w-5 h-5 text-navy-600" /></div>
                <div><h3 className="font-body font-bold text-gray-800 text-base">Manage Content</h3><p className="text-sm text-gray-500 font-body">Edit series & videos</p></div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-navy-500 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
          <Link to="/admin/analytics" data-testid="view-analytics-link"
            className="block bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md hover:border-sage-200 transition-all group">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-edupaws-sage/20 rounded-lg flex items-center justify-center"><BarChart3 className="w-5 h-5 text-edupaws-sage" /></div>
                <div><h3 className="font-body font-bold text-gray-800 text-base">View Analytics</h3><p className="text-sm text-gray-500 font-body">Detailed statistics</p></div>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-edupaws-sage group-hover:translate-x-1 transition-all" />
            </div>
          </Link>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-gray-800 flex items-center gap-2 uppercase">
              <MessageSquare className="w-5 h-5 text-navy-600" /> Community Announcements
            </h2>
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="new-announcement-btn"
                  className="flex items-center gap-2 bg-navy-600 text-white px-4 py-2 rounded-xl font-body font-bold text-sm
                    shadow-[0_3px_0_#2a3a4d] hover:shadow-[0_1px_0_#2a3a4d] hover:translate-y-[2px] transition-all">
                  <Send className="w-4 h-4" /> New Announcement
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-heading text-xl text-navy-600 uppercase">Send Community Announcement</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="space-y-2">
                    <Label className="font-body font-bold text-base">Title</Label>
                    <Input data-testid="announcement-title-input" value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                      placeholder="e.g., New Series Released!" className="h-12 rounded-xl text-base" />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-body font-bold text-base">Message</Label>
                    <textarea data-testid="announcement-message-input" value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                      placeholder="Write your message to the community..." rows={4}
                      className="w-full rounded-xl border border-gray-200 px-4 py-3 font-body text-base focus:outline-none focus:ring-2 focus:ring-navy-300 resize-none" />
                  </div>
                  <button onClick={handleSendAnnouncement} data-testid="send-announcement-btn"
                    className="w-full bg-navy-600 text-white py-3 rounded-xl font-heading font-bold text-lg uppercase
                      shadow-[0_4px_0_#2a3a4d] hover:shadow-[0_2px_0_#2a3a4d] hover:translate-y-[2px] transition-all">
                    Send to Community
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
            {announcements.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 font-body text-base">No announcements yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {announcements.slice(0, 5).map((ann) => (
                  <div key={ann.id} className="p-4 flex items-start gap-3 group" data-testid={`announcement-${ann.id}`}>
                    <div className="w-8 h-8 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare className="w-4 h-4 text-navy-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-body font-bold text-gray-800 text-base">{ann.title}</div>
                      <p className="text-gray-500 font-body text-sm mt-0.5 line-clamp-2">{ann.message}</p>
                      <div className="text-xs text-gray-400 font-body mt-1">
                        {new Date(ann.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteAnnouncement(ann.id)}
                      className="text-gray-300 hover:text-red-500 text-xs opacity-0 group-hover:opacity-100 transition-opacity">Remove</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ===== Orders Tab =====

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [fulfillmentForm, setFulfillmentForm] = useState({ box_number: 1, tracking_number: '', carrier: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchOrders(); }, [filter]);

  const fetchOrders = async () => {
    try {
      const [ordersRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/orders?status=${filter}`),
        axios.get(`${API}/admin/orders/stats`)
      ]);
      setOrders(ordersRes.data || []);
      setStats(statsRes.data || {});
    } catch { toast.error('Failed to load orders'); }
    finally { setLoading(false); }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status });
      toast.success('Order status updated');
      fetchOrders();
    } catch { toast.error('Failed to update order'); }
  };

  const addFulfillment = async (orderId) => {
    if (!fulfillmentForm.tracking_number.trim()) {
      toast.error('Please enter a tracking number');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/orders/${orderId}/fulfillment`, fulfillmentForm);
      toast.success(`Box ${fulfillmentForm.box_number} tracking added! Videos unlocked & email sent.`);
      setFulfillmentForm({ box_number: 1, tracking_number: '', carrier: '' });
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add fulfillment');
    } finally { setSubmitting(false); }
  };

  const getShippedBoxes = (order) => {
    return (order.fulfillments || []).map(f => f.box_number);
  };

  const getNextBoxNumber = (order) => {
    const shipped = getShippedBoxes(order);
    for (let i = 1; i <= (order.total_boxes || 10); i++) {
      if (!shipped.includes(i)) return i;
    }
    return null;
  };

  const STATUS_COLORS = {
    pending: 'bg-amber-100 text-amber-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-green-100 text-green-700',
  };

  return (
    <>
      {/* Order Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Orders', value: stats.total || 0, icon: Package, bg: 'bg-navy-100', fg: 'text-navy-600' },
          { label: 'Pending', value: stats.pending || 0, icon: Clock, bg: 'bg-amber-100', fg: 'text-amber-600' },
          { label: 'Shipped', value: stats.shipped || 0, icon: Truck, bg: 'bg-blue-100', fg: 'text-blue-600' },
          { label: 'Delivered', value: stats.delivered || 0, icon: CheckCircle, bg: 'bg-green-100', fg: 'text-green-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.fg}`} />
              </div>
              <div className="text-sm font-body text-gray-500 uppercase tracking-wide">{s.label}</div>
            </div>
            <div className="font-heading text-4xl font-bold text-gray-800">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-gray-400" />
        {['all', 'pending', 'shipped', 'delivered'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
            className={`px-4 py-2 rounded-full text-sm font-body font-bold transition-colors ${
              filter === f ? 'bg-edupaws-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-edupaws-sage border-t-transparent mx-auto"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-body text-base">No orders yet</p>
            <p className="text-gray-400 font-body text-sm mt-1">Orders will appear here automatically when payments are processed</p>
          </div>
        ) : orders.map((order) => {
          const isExpanded = expandedOrder === order.id;
          const fulfillments = order.fulfillments || [];
          const shippedBoxes = getShippedBoxes(order);
          const nextBox = getNextBoxNumber(order);
          const totalBoxes = order.total_boxes || 10;
          const isSubscriptionType = ['box_subscription', 'subscription_renewal', 'full_package', 'accelerated'].includes(order.payment_type);

          return (
            <div key={order.id} data-testid={`order-row-${order.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Order Header Row */}
              <div
                className={`px-5 py-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpanded ? 'border-b border-gray-100' : ''}`}
                onClick={() => { setExpandedOrder(isExpanded ? null : order.id); if (!isExpanded && nextBox) setFulfillmentForm({ box_number: nextBox, tracking_number: '', carrier: '' }); }}
                data-testid={`order-expand-${order.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-body font-bold text-gray-800 text-sm">{order.user_name}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-body font-bold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-500'}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 font-body">{order.user_email}</div>
                  {order.shipping_address && (
                    <div className="mt-1 flex items-start gap-1 text-xs text-gray-400 font-body">
                      <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>{order.shipping_address.address_line1}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}</span>
                    </div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="font-heading font-bold text-gray-800">${order.amount}</div>
                  <div className="text-xs text-gray-500 font-body">
                    {new Date(order.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
                {isSubscriptionType && (
                  <div className="flex-shrink-0 text-center px-2">
                    <div className="text-xs font-body text-gray-400 mb-1">Boxes</div>
                    <div className="font-heading font-bold text-sm text-edupaws-navy">
                      {shippedBoxes.length}/{totalBoxes}
                    </div>
                  </div>
                )}
                <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
              </div>

              {/* Expanded Fulfillment Panel */}
              {isExpanded && (
                <div className="px-5 py-4 bg-gray-50/50" data-testid={`order-fulfillment-panel-${order.id}`}>
                  {/* Box Timeline / Progress */}
                  {isSubscriptionType && (
                    <div className="mb-5">
                      <h4 className="font-body font-bold text-sm text-gray-700 mb-3">Box Fulfillment Progress</h4>
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: totalBoxes }, (_, i) => i + 1).map((boxNum) => {
                          const fulfillment = fulfillments.find(f => f.box_number === boxNum);
                          const isShipped = !!fulfillment;
                          return (
                            <div
                              key={boxNum}
                              data-testid={`box-indicator-${order.id}-${boxNum}`}
                              className={`relative flex flex-col items-center px-3 py-2 rounded-xl border text-xs font-body transition-all ${
                                isShipped
                                  ? 'bg-edupaws-sage/10 border-edupaws-sage text-edupaws-sage'
                                  : boxNum === nextBox
                                    ? 'bg-amber-50 border-amber-300 text-amber-700 ring-2 ring-amber-200'
                                    : 'bg-white border-gray-200 text-gray-400'
                              }`}
                              title={isShipped ? `Tracking: ${fulfillment.tracking_number}` : `Box ${boxNum} - Not shipped`}
                            >
                              <div className="font-bold mb-0.5">Box {boxNum}</div>
                              {isShipped ? (
                                <div className="flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  <span className="text-[10px]">Shipped</span>
                                </div>
                              ) : boxNum === nextBox ? (
                                <span className="text-[10px] font-bold">Next</span>
                              ) : (
                                <span className="text-[10px]">Pending</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Existing Fulfillments */}
                  {fulfillments.length > 0 && (
                    <div className="mb-5">
                      <h4 className="font-body font-bold text-sm text-gray-700 mb-2">Shipped Boxes</h4>
                      <div className="space-y-2">
                        {fulfillments.map((f, idx) => (
                          <div key={idx} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2 border border-gray-100 text-sm">
                            <Package className="w-4 h-4 text-edupaws-sage flex-shrink-0" />
                            <span className="font-body font-bold text-gray-700">Box {f.box_number}</span>
                            <span className="font-body text-gray-500">{f.tracking_number}</span>
                            {f.carrier && <span className="text-xs text-gray-400 font-body">({f.carrier})</span>}
                            <span className="text-xs text-gray-400 font-body ml-auto">
                              {new Date(f.shipped_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add New Fulfillment Form */}
                  {isSubscriptionType && nextBox && (
                    <div className="bg-white rounded-xl border border-gray-200 p-4">
                      <h4 className="font-body font-bold text-sm text-gray-700 mb-3">Add Tracking for Box {nextBox}</h4>
                      <div className="flex flex-wrap items-end gap-3">
                        <div>
                          <Label className="text-xs font-body text-gray-500 mb-1">Box #</Label>
                          <select
                            data-testid={`fulfillment-box-select-${order.id}`}
                            value={fulfillmentForm.box_number}
                            onChange={(e) => setFulfillmentForm(prev => ({ ...prev, box_number: parseInt(e.target.value) }))}
                            className="h-9 text-sm font-body border border-gray-200 rounded-lg px-2 bg-white w-20"
                          >
                            {Array.from({ length: totalBoxes }, (_, i) => i + 1)
                              .filter(n => !shippedBoxes.includes(n))
                              .map(n => <option key={n} value={n}>Box {n}</option>)}
                          </select>
                        </div>
                        <div className="flex-1 min-w-[160px]">
                          <Label className="text-xs font-body text-gray-500 mb-1">Tracking Number</Label>
                          <Input
                            data-testid={`fulfillment-tracking-input-${order.id}`}
                            value={fulfillmentForm.tracking_number}
                            onChange={(e) => setFulfillmentForm(prev => ({ ...prev, tracking_number: e.target.value }))}
                            placeholder="Enter tracking number"
                            className="h-9 text-sm"
                          />
                        </div>
                        <div className="w-28">
                          <Label className="text-xs font-body text-gray-500 mb-1">Carrier</Label>
                          <select
                            data-testid={`fulfillment-carrier-select-${order.id}`}
                            value={fulfillmentForm.carrier}
                            onChange={(e) => setFulfillmentForm(prev => ({ ...prev, carrier: e.target.value }))}
                            className="h-9 text-sm font-body border border-gray-200 rounded-lg px-2 bg-white w-full"
                          >
                            <option value="">Select</option>
                            <option value="USPS">USPS</option>
                            <option value="UPS">UPS</option>
                            <option value="FedEx">FedEx</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        <button
                          data-testid={`fulfillment-submit-${order.id}`}
                          onClick={() => addFulfillment(order.id)}
                          disabled={submitting}
                          className="h-9 px-4 bg-edupaws-navy text-white rounded-lg text-sm font-body font-bold hover:bg-edupaws-navy/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                        >
                          <Truck className="w-4 h-4" />
                          {submitting ? 'Saving...' : 'Ship Box'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 font-body mt-2">
                        Adding tracking will auto-unlock Box {fulfillmentForm.box_number} videos and send a shipping notification email.
                      </p>
                    </div>
                  )}

                  {isSubscriptionType && !nextBox && (
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4 text-center">
                      <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
                      <p className="font-body font-bold text-green-700 text-sm">All {totalBoxes} boxes have been shipped!</p>
                    </div>
                  )}

                  {/* Status Update */}
                  <div className="mt-4 flex items-center gap-3">
                    <Label className="text-xs font-body text-gray-500">Order Status:</Label>
                    <select
                      data-testid={`order-status-select-${order.id}`}
                      value={order.status}
                      onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                      className="text-xs font-body border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                    >
                      <option value="pending">Pending</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

// ===== Messages Tab =====

function MessagesTab() {
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [adminId, setAdminId] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchConversations(); }, []);

  const fetchConversations = async () => {
    try {
      const [convosRes, trainerRes] = await Promise.all([
        axios.get(`${API}/admin/messages/conversations`),
        axios.get(`${API}/trainer/info`)
      ]);
      setConversations(convosRes.data || []);
      setAdminId(trainerRes.data.id);
    } catch { console.error('Error loading conversations'); }
    finally { setLoading(false); }
  };

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const res = await axios.get(`${API}/messages/conversation/${adminId}/${convo.user_id}`);
      setMessages(res.data || []);
    } catch { toast.error('Failed to load messages'); }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConvo) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, {
        sender_id: adminId,
        sender_name: 'Edu Paws Team',
        receiver_id: activeConvo.user_id,
        message: newMessage.trim(),
        sender_mode: 'admin'
      });
      setNewMessage('');
      const res = await axios.get(`${API}/messages/conversation/${adminId}/${activeConvo.user_id}`);
      setMessages(res.data || []);
      fetchConversations();
    } catch { toast.error('Failed to send'); }
    finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-edupaws-sage border-t-transparent"></div></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" style={{ minHeight: '60vh' }}>
      {/* Conversations List */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-heading font-bold text-gray-800 uppercase text-sm">Conversations</h3>
        </div>
        {conversations.length === 0 ? (
          <div className="p-8 text-center">
            <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm font-body">No messages yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {conversations.map((convo) => (
              <div
                key={convo.user_id}
                data-testid={`admin-convo-${convo.user_id}`}
                onClick={() => openConversation(convo)}
                className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                  activeConvo?.user_id === convo.user_id ? 'bg-navy-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-navy-100 rounded-full flex items-center justify-center font-heading font-bold text-navy-600 text-sm">
                      {convo.user_name.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <div className="font-body font-bold text-gray-800 text-sm truncate">{convo.user_name}</div>
                      <div className="text-xs text-gray-400 font-body truncate">{convo.last_message}</div>
                    </div>
                  </div>
                  {convo.unread > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{convo.unread}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Chat Area */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
        {!activeConvo ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 font-body">
            <div className="text-center">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Select a conversation</p>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-edupaws-navy px-6 py-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-heading font-bold text-white">
                {activeConvo.user_name.charAt(0)}
              </div>
              <div>
                <div className="text-white font-heading font-bold">{activeConvo.user_name}</div>
                <div className="text-white/60 text-sm font-body">{activeConvo.user_email}</div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50/50" data-testid="admin-messages-list">
              {messages.map((msg) => {
                const isAdmin = msg.sender_id === adminId;
                return (
                  <div key={msg.id} className={`flex ${isAdmin ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] px-4 py-3 rounded-2xl font-body text-sm ${
                      isAdmin ? 'bg-edupaws-navy text-white rounded-br-md' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
                    }`}>
                      <p>{msg.message}</p>
                      <div className={`text-[10px] mt-1 ${isAdmin ? 'text-white/60' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-4 border-t border-gray-100">
              <div className="flex gap-2">
                <Input value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                  placeholder="Reply..." className="flex-1 h-12 rounded-xl" disabled={sending}
                  data-testid="admin-message-input" />
                <button onClick={handleSend} disabled={sending || !newMessage.trim()}
                  data-testid="admin-send-msg-btn"
                  className="h-12 px-5 bg-edupaws-navy text-white rounded-xl font-heading font-bold flex items-center gap-2
                    hover:bg-edupaws-navy/90 disabled:opacity-40 transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ===== Quiz Leads Tab =====

function QuizLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNature, setFilterNature] = useState('all');

  useEffect(() => { fetchLeads(); }, []);

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/admin/quiz-leads`);
      setLeads(res.data || []);
    } catch { toast.error('Failed to load quiz leads'); }
    finally { setLoading(false); }
  };

  const filtered = leads.filter(lead => {
    const matchesSearch = !searchTerm || 
      (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (lead.dog_nature && lead.dog_nature.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (lead.human_pattern && lead.human_pattern.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesNature = filterNature === 'all' || lead.dog_nature === filterNature;
    return matchesSearch && matchesNature;
  });

  const withEmail = leads.filter(l => l.email);
  const natures = ['Director', 'Peacemaker', 'Empath'];

  const NATURE_COLORS = {
    Director: 'bg-blue-100 text-blue-700',
    Peacemaker: 'bg-green-100 text-green-700',
    Empath: 'bg-purple-100 text-purple-700',
  };

  const TIER_COLORS = {
    'Full Package': 'bg-edupaws-sage/20 text-edupaws-sage',
    'Accelerated': 'bg-amber-100 text-amber-700',
    'Monthly': 'bg-navy-100 text-navy-600',
  };

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Quizzes', value: leads.length, icon: ClipboardList, bg: 'bg-navy-100', fg: 'text-navy-600' },
          { label: 'With Email', value: withEmail.length, icon: Mail, bg: 'bg-green-100', fg: 'text-green-600' },
          { label: 'Anonymous', value: leads.length - withEmail.length, icon: Users, bg: 'bg-gray-100', fg: 'text-gray-600' },
          { label: 'Capture Rate', value: leads.length > 0 ? `${Math.round((withEmail.length / leads.length) * 100)}%` : '0%', icon: TrendingUp, bg: 'bg-edupaws-sage/20', fg: 'text-edupaws-sage' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-testid={`lead-stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.fg}`} />
              </div>
              <div className="text-sm font-body text-gray-500 uppercase tracking-wide">{s.label}</div>
            </div>
            <div className="font-heading text-4xl font-bold text-gray-800">{s.value}</div>
          </div>
        ))}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            data-testid="leads-search"
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by email, nature, or pattern..." className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-400" />
          {['all', ...natures].map((n) => (
            <button key={n} onClick={() => setFilterNature(n)}
              data-testid={`filter-nature-${n.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm font-body font-bold transition-colors ${
                filterNature === n ? 'bg-edupaws-navy text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {n === 'all' ? 'All' : n}
            </button>
          ))}
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-edupaws-sage border-t-transparent mx-auto"></div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-body text-base">No quiz leads found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 font-body font-bold text-xs text-gray-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 font-body font-bold text-xs text-gray-500 uppercase">Dog Nature</th>
                  <th className="text-left px-4 py-3 font-body font-bold text-xs text-gray-500 uppercase">Human Pattern</th>
                  <th className="text-left px-4 py-3 font-body font-bold text-xs text-gray-500 uppercase">Recommended Tier</th>
                  <th className="text-left px-4 py-3 font-body font-bold text-xs text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      {lead.email ? (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-edupaws-sage" />
                          <span className="font-body font-bold text-gray-800 text-sm">{lead.email}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400 font-body text-sm italic">No email provided</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {lead.dog_nature ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-body font-bold ${NATURE_COLORS[lead.dog_nature] || 'bg-gray-100 text-gray-500'}`}>
                          {lead.dog_nature}
                        </span>
                      ) : <span className="text-gray-400 text-sm">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-body text-gray-700">{lead.human_pattern || '-'}</td>
                    <td className="px-4 py-3">
                      {lead.best_match_tier ? (
                        <span className={`px-3 py-1 rounded-full text-xs font-body font-bold ${TIER_COLORS[lead.best_match_tier] || 'bg-gray-100 text-gray-500'}`}>
                          {lead.best_match_tier}
                        </span>
                      ) : <span className="text-gray-400 text-sm">-</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-body text-gray-500">
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}


// ===== Main Admin Dashboard =====

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [owners, setOwners] = useState([]);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, announcementsRes, ownersRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/announcements`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/owners`).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/sessions`).catch(() => ({ data: [] }))
      ]);
      setAnalytics(analyticsRes.data);
      setAnnouncements(announcementsRes.data || []);
      setOwners(ownersRes.data || []);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-edupaws-sage border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  const TABS = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: ClipboardList },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
    { id: 'inventory', label: 'Inventory', icon: Warehouse },
    { id: 'orders', label: 'Orders', icon: Package },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
  ];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 data-testid="admin-dashboard-title" className="font-heading text-4xl font-bold text-gray-800 mb-2 uppercase tracking-wide">
          Admin Dashboard
        </h1>
        <p className="text-gray-500 font-body text-lg">Manage your dog psychology curriculum and community</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 mb-8 border-b border-gray-200 pb-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-3 font-body font-bold text-sm border-b-2 transition-colors -mb-[1px] ${
              activeTab === tab.id
                ? 'border-edupaws-navy text-edupaws-navy'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          analytics={analytics} owners={owners} sessions={sessions}
          announcements={announcements} selectedOwner={selectedOwner}
          setSelectedOwner={setSelectedOwner} fetchData={fetchData}
        />
      )}
      {activeTab === 'leads' && <QuizLeadsTab />}
      {activeTab === 'orders' && <OrdersTab />}
      {activeTab === 'sessions' && <SessionsTab />}
      {activeTab === 'inventory' && <InventoryTab />}
      {activeTab === 'messages' && <MessagesTab />}
    </AdminLayout>
  );
}
